import { useState, useEffect } from 'react';
import { useApi } from '@/hooks/useApi';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { PhoneInput } from '@/components/ui/phone-input';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, RotateCcw, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { useI18n } from '@/contexts/I18nContext';

type DebtorType = 'carrier' | 'tenant' | 'platform';

interface CreateRefundModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

export function CreateRefundModal({ open, onOpenChange, onSuccess }: CreateRefundModalProps) {
  const { t } = useI18n();
  const { post, loading } = useApi({ showSuccessToast: false, showErrorToast: false });

  const [debtorType, setDebtorType] = useState<DebtorType>('carrier');
  const [debtorId, setDebtorId] = useState<string>('');
  const [shipmentId, setShipmentId] = useState<string>('');
  const [customerName, setCustomerName] = useState<string>('');
  const [customerPhone, setCustomerPhone] = useState<string>('');
  const [customerIban, setCustomerIban] = useState<string>('');
  const [amount, setAmount] = useState<string>('');
  const [reason, setReason] = useState<string>('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setDebtorType('carrier');
      setDebtorId('');
      setShipmentId('');
      setCustomerName('');
      setCustomerPhone('');
      setCustomerIban('');
      setAmount('');
      setReason('');
      setError(null);
    }
  }, [open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const parsedAmount = parseFloat(amount);
    if (isNaN(parsedAmount) || parsedAmount <= 0) {
      setError('يرجى إدخال مبلغ صحيح');
      return;
    }
    if (!debtorId.trim()) {
      setError('يرجى إدخال معرّف المدين');
      return;
    }
    if (!reason.trim()) {
      setError('يرجى إدخال سبب المرتجع');
      return;
    }

    const customer_metadata: Record<string, string> = {};
    if (customerName.trim()) customer_metadata.name = customerName.trim();
    if (customerPhone.trim()) customer_metadata.phone = customerPhone.trim();
    if (customerIban.trim()) customer_metadata.iban = customerIban.trim();

    try {
      await post('/api/v1/refunds', {
        debtor_type: debtorType,
        debtor_id: debtorId.trim(),
        shipment_id: shipmentId.trim() || undefined,
        customer_metadata,
        amount: parsedAmount,
        reason: reason.trim(),
      });
      toast.success(t.financials.refund_created);
      onSuccess();
      onOpenChange(false);
    } catch (err: any) {
      const msg = err?.message ?? 'فشل في إنشاء المرتجع';
      setError(msg);
      toast.error(msg);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[520px] max-h-[90vh] overflow-y-auto" dir="rtl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <RotateCcw className="h-4 w-4" />
            {t.financials.create_refund}
          </DialogTitle>
          <DialogDescription className="text-xs">
            إنشاء طلب مرتجع يدوي
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-2">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label htmlFor="rf-debtor-type">
                {t.financials.debtor} <span className="text-destructive">*</span>
              </Label>
              <Select value={debtorType} onValueChange={(v) => setDebtorType(v as DebtorType)} disabled={loading}>
                <SelectTrigger id="rf-debtor-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="carrier">شركة شحن</SelectItem>
                  <SelectItem value="tenant">تاجر</SelectItem>
                  <SelectItem value="platform">المنصة</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="rf-debtor-id">
                معرّف المدين <span className="text-destructive">*</span>
              </Label>
              <Input
                id="rf-debtor-id"
                value={debtorId}
                onChange={(e) => setDebtorId(e.target.value)}
                disabled={loading}
                dir="ltr"
                className="text-start"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="rf-shipment">معرّف الشحنة (اختياري)</Label>
            <Input
              id="rf-shipment"
              value={shipmentId}
              onChange={(e) => setShipmentId(e.target.value)}
              disabled={loading}
              dir="ltr"
              className="text-start"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label htmlFor="rf-cust-name">{t.financials.customer_name}</Label>
              <Input
                id="rf-cust-name"
                value={customerName}
                onChange={(e) => setCustomerName(e.target.value)}
                disabled={loading}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="rf-cust-phone">{t.financials.customer_phone}</Label>
              <PhoneInput
                value={customerPhone}
                onChange={setCustomerPhone}
                disabled={loading}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="rf-iban">{t.financials.customer_iban}</Label>
            <Input
              id="rf-iban"
              value={customerIban}
              onChange={(e) => setCustomerIban(e.target.value)}
              disabled={loading}
              dir="ltr"
              className="text-start"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="rf-amount">
              {t.financials.amount} <span className="text-destructive">*</span>
            </Label>
            <Input
              id="rf-amount"
              type="number"
              min={0}
              step="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              disabled={loading}
              dir="ltr"
              className="text-start"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="rf-reason">
              {t.financials.refund_reason} <span className="text-destructive">*</span>
            </Label>
            <Textarea
              id="rf-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              disabled={loading}
              rows={3}
              className="resize-none"
            />
          </div>

          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <DialogFooter className="gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={loading}
            >
              إلغاء
            </Button>
            <Button type="submit" disabled={loading} className="gap-2">
              {loading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <RotateCcw className="h-4 w-4" />
              )}
              {t.financials.create_refund}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
